import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/store/auth";
import type { ApiError } from "@/types";

// Prefer Vite's import.meta.env (not process.env — that doesn't exist in Vite).
// Falls back to the dev-proxy path when no override is provided.
export const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
  // Backend LLM timeout is 180 s — front-end must be at least as long so
  // complex SOAP notes don't trigger a network error while the server is still
  // running. Add 30 s of overhead for DB + network round-trip on top of that.
  timeout: 210_000,
});

// Attach auth token on every request
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Normalise error shape so components get a predictable object.
// Also: on 401, clear the auth store — a stale token should boot the user.
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().clear();
    }

    const response = error.response;
    if (!response) {
      return Promise.reject<ApiError>({ error: "NETWORK_ERROR", message: error.message || "Request failed" });
    }

    // Keep the status so callers can tell a 429 or a missing endpoint from other failures.
    // FastAPI's own errors carry `detail` rather than `message`.
    const body = (typeof response.data === "object" && response.data) || ({} as Partial<ApiError> & { detail?: unknown });
    const detail = "detail" in body && typeof body.detail === "string" ? body.detail : undefined;
    const retryAfter = Number(response.headers?.["retry-after"]);
    const normalised: ApiError = {
      ...body,
      error: body.error ?? `HTTP_${response.status}`,
      message: body.message ?? detail ?? error.message ?? "Request failed",
      status: response.status,
      ...(Number.isFinite(retryAfter) && retryAfter > 0 ? { retry_after_seconds: retryAfter } : {}),
    };

    return Promise.reject(normalised);
  },
);
